import type WaInput from "@web.awesome.me/webawesome-pro/dist/components/input/input.js";
import { html, type PropertyValues } from "lit";
import { live } from "lit/directives/live.js";
import { z } from "zod";
import { checkNotNil } from "../../check-not-nil.js";
import { FormAssociationMixin } from "../../mixins/form-associated-element.js";
import { attributeProperty } from "../../property.js";
import type { ParserType } from "../../schemas/parser.js";
import { WmElement } from "../element.js";

type Value = ParserType<typeof Value>;
const Value = attributeProperty("value", z.string());

type ValueError = ParserType<typeof ValueError>;
const ValueError = attributeProperty("error", z.string());

export class WmNoteTitle extends FormAssociationMixin(WmElement) {
  static readonly TAG_NAME = "wm-note-title";

  static properties = {
    ...super.properties,
    value: Value,
    error: ValueError,
  };

  declare value: Value;
  declare error: ValueError;

  constructor() {
    super();
    this.name = "title";
    this.value = undefined;
    this.error = undefined;
  }

  protected override render(): unknown {
    return html`
      <wa-input
        type="text"
        label="Title"
        hint="Customize or leave this field autopopulated"
        required
        .value=${live(this.value)}
        @input=${this.#handleInput}></wa-input>
    `;
  }

  protected override updated(_: PropertyValues): void {
    const input = this.queryNotNil("wa-input");
    input.updateComplete.then(() => {
      const validationTarget = checkNotNil(input.validationTarget);
      this.internals.setFormValue(this.value ?? "");

      // If a custom error is set, it takes precedence, and triggers an
      // immediate report.
      if (this.error) {
        this.internals.setValidity(
          { customError: true },
          this.error,
          validationTarget,
        );
        this.reportValidity();
        // Otherwise, the validation is delegated to Web Awesome's component.
      } else {
        input.updateValidity();
        this.internals.setValidity(
          input.validity,
          input.validationMessage,
          validationTarget,
        );
      }
    });
  }

  #handleInput(e: InputEvent) {
    const target = checkNotNil(e.target) as WaInput;
    this.value = target.value;
    // Resets the custom error, if any.
    this.error = undefined;
  }
}

customElements.define(WmNoteTitle.TAG_NAME, WmNoteTitle);

declare global {
  interface HTMLElementTagNameMap {
    [WmNoteTitle.TAG_NAME]: WmNoteTitle;
  }
}
